// src/components/Sales.jsx
// Composant principal de vente - Version refactorisée
import React, { useMemo, useState, useEffect } from 'react';
import { apiFetch, apiFetchBlob, downloadBlob } from '../api.js';
import { calcTotalsWithRemise } from './Remise.jsx';

// Composants extraits
import {
  CustomerAutocomplete,
  ProductSelector,
  CartTable,
  SalesSummary,
  PaymentModeSelector,
  groupVariantsByType,
  getDefaultSelectedVariants,
  computeUnitPriceForLine,
  buildVariantsFromComposition,
} from './sales/index.js';

function clampInt(n, min, max, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  const y = Math.trunc(x);
  return Math.max(min, Math.min(max, y));
}

function clampNumber(n, min, max, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.max(min, Math.min(max, x));
}

function toYYYYMMDD(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
}

function availableStock(p) {
  return Number(p?.stock_quantity ?? p?.stock ?? 0);
}

/* ============================================================
 *                     SALES (principal)
 * ============================================================ */
export default function Sales({ products = [], reloadProducts, onCreate }) {
  // Client autocomplete state
  const [buyer, setBuyer] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // TVA + Jour de vente
  const [tvaPct, setTvaPct] = useState(0);
  const [saleDate, setSaleDate] = useState(toYYYYMMDD(new Date()));

  // Produits / lignes
  const [pid, setPid] = useState('');
  const [qty, setQty] = useState(1);
  const [lines, setLines] = useState([]);

  // Remise
  const [discount, setDiscount] = useState({ remise_pct: 0, remise_amount: 0 });

  // RFM du client sélectionné
  const [customerRfm, setCustomerRfm] = useState(null);
  const [rfmDiscounts, setRfmDiscounts] = useState(null);
  const [rfmLoading, setRfmLoading] = useState(false);

  // Paiement: cash ou tranches + méthode
  const [paymentMode, setPaymentMode] = useState({
    type: 'cash',
    options: { method: 'cash', provider: 'orange_money', reference: '', note: '', paid_now: true },
  });

  // Charger les remises RFM configurées au montage
  useEffect(() => {
    apiFetch('/api/settings/rfm-discounts').then(d => setRfmDiscounts(d)).catch(() => {})
  }, [])

  // Charger le score RFM quand un client est sélectionné
  useEffect(() => {
    if (!selectedCustomer?.uid) { setCustomerRfm(null); return }
    setRfmLoading(true)
    apiFetch(`/api/analytics/rfm/customer/${selectedCustomer.uid}`)
      .then(d => setCustomerRfm(d))
      .catch(() => setCustomerRfm(null))
      .finally(() => setRfmLoading(false))
  }, [selectedCustomer?.uid])

  // Cache variants par product_id
  const [variantsCache, setVariantsCache] = useState({});
  const [variantsLoading, setVariantsLoading] = useState(false);

  // Fetch variants pour un produit
  async function fetchVariantsForProduct(productId, { force = false } = {}) {
    if (!productId) return { list: [], byType: {} };
    if (!force && variantsCache[productId]) return variantsCache[productId];

    setVariantsLoading(true);
    try {
      const attempts = [
        `/api/products/${productId}/variants?ts=${Date.now()}`,
        `/api/product_variants?product_id=${encodeURIComponent(productId)}&ts=${Date.now()}`,
        `/api/product-variants?product_id=${encodeURIComponent(productId)}&ts=${Date.now()}`,
      ];

      let list = null;
      let rulesByType = {};
      let rulesConfigured = false;
      for (const url of attempts) {
        try {
          const data = await apiFetch(url);
          const arr = Array.isArray(data) ? data : Array.isArray(data?.variants) ? data.variants : null;
          if (data?.variant_rules) rulesByType = data.variant_rules || {};
          if (data?.variant_rules_configured) rulesConfigured = !!data.variant_rules_configured;
          if (arr) {
            list = arr;
            break;
          }
        } catch {
          // ignore
        }
      }

      const finalList = Array.isArray(list) ? list : [];
      const byType = groupVariantsByType(finalList);
      const payload = { list: finalList, byType, rulesByType, rulesConfigured };
      setVariantsCache(prev => ({ ...prev, [productId]: payload }));
      return payload;
    } finally {
      setVariantsLoading(false);
    }
  }

  useEffect(() => {
    if (!pid) return;
    fetchVariantsForProduct(pid, { force: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  // Ajouter une ligne au panier
  function addLine() {
    const p = products.find(x => x.id === pid);
    if (!p) return alert('Choisissez un produit');

    const q = Number(qty);
    if (!Number.isFinite(q) || q <= 0) return alert('Quantité invalide');

    const remain = availableStock(p);
    if (q > remain) {
      return alert(`Stock insuffisant pour ${p.name} (reste ${remain})`);
    }

    const cached = variantsCache[p.id] || { list: [], byType: {}, rulesByType: {}, rulesConfigured: false };
    const selectedVariants = getDefaultSelectedVariants(cached.byType);

    const unitPrice = computeUnitPriceForLine({
      productUnitPrice: Number(p?.unit_price ?? 0),
      selectedVariants,
      byType: cached.byType || {},
      rulesByType: cached.rulesByType || {},
      rulesConfigured: cached.rulesConfigured,
    });

    setLines(l => [
      ...l,
      {
        id: Date.now() + Math.random(),
        productId: p.id,
        name: p.name,
        qty: q,
        composition: { ...(selectedVariants || {}), notes: '' },
        selectedVariants,
        unitPrice,
      },
    ]);

    setQty(1);
  }

  function removeLine(id) {
    setLines(prev => prev.filter(x => x.id !== id));
  }

  function updateLineQty(id, nextQty) {
    setLines(prev =>
      prev.map(l => (l.id === id ? { ...l, qty: clampInt(nextQty, 1, 999999, 1) } : l))
    );
  }

  function updateLineVariant(id, variant_type, code) {
    setLines(prev =>
      prev.map(l => {
        if (l.id !== id) return l;

        const cached = variantsCache[l.productId] || { byType: {}, rulesByType: {}, rulesConfigured: false };
        const byType = cached.byType || {};

        const nextSelected = { ...(l.selectedVariants || {}), [variant_type]: code };
        const nextComposition = { ...(l.composition || {}), [variant_type]: code };

        if (variant_type === 'type_bougie' && String(code) === 'SANS') {
          nextSelected.nb_bougies = '0';
          nextComposition.nb_bougies = '0';
        }

        const product = products.find(x => x.id === l.productId);
        const basePU = Number(product?.unit_price ?? l.unitPrice ?? 0);

        const nextUnitPrice = computeUnitPriceForLine({
          productUnitPrice: basePU,
          selectedVariants: nextSelected,
          byType,
          rulesByType: cached.rulesByType || {},
          rulesConfigured: cached.rulesConfigured,
        });

        return { ...l, selectedVariants: nextSelected, composition: nextComposition, unitPrice: nextUnitPrice };
      })
    );
  }

  function updateLineNotes(id, notes) {
    setLines(prev =>
      prev.map(l => (l.id === id ? { ...l, composition: { ...(l.composition || {}), notes } } : l))
    );
  }

  // Totaux HT
  const totalHT = useMemo(() => {
    return lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitPrice || 0), 0);
  }, [lines]);

  // TVA safe
  const safeTva = useMemo(() => clampNumber(tvaPct, 0, 100, 0), [tvaPct]);

  // Totaux avec remise
  const totalsWithDiscount = useMemo(() => {
    return calcTotalsWithRemise({
      totalHT,
      tvaPct: safeTva,
      remisePct: discount?.remise_pct,
      remiseAmount: discount?.remise_amount,
    });
  }, [totalHT, safeTva, discount]);

  const totalTTC = totalsWithDiscount.ttc;

  // Garde-fou: date future
  function safeSetSaleDate(v) {
    const s = String(v || '').trim();
    if (!s) return setSaleDate('');
    const today = toYYYYMMDD(new Date());
    if (s > today) return alert('Jour de vente ne peut pas être dans le futur.');
    setSaleDate(s);
  }

  // Création de vente
  async function createOrder(wantPdf) {
    if (!buyer.trim()) return alert('Nom acheteur requis');
    if (lines.length === 0) return alert('Ajoutez au moins un produit');

    for (const l of lines) {
      const p = products.find(x => x.id === l.productId);
      if (p) {
        const remain = availableStock(p);
        if (l.qty > remain) return alert(`Stock insuffisant pour ${p.name} (reste ${remain})`);
      }
    }

    try {
      const isInst = paymentMode?.type === 'installments';

      const payload = {
        buyer_name: buyer.trim(),
        buyer_phone: (phone || '').trim(),
        tva_pct: Number.isFinite(Number(safeTva)) ? Number(safeTva) : 18,
        remise_pct: Number(discount?.remise_pct || 0),
        remise_amount: Number(discount?.remise_amount || 0),
        sale_date: saleDate || null,
        generate_pdf: !!wantPdf,
        customer_uid: selectedCustomer?.uid || null,

        lines: lines.map(l => {
          const cached = variantsCache[l.productId] || { byType: {}, rulesByType: {}, rulesConfigured: false };
          const byType = cached.byType || {};
          const variants = buildVariantsFromComposition(l.composition, byType);

          return {
            product_id: l.productId,
            qty: Number(l.qty || 1),
            variants,
            composition: l.composition || {},
          };
        }),

        payment_mode: isInst ? 'installments' : 'cash',
        payment_method: !isInst ? (paymentMode?.options?.method || undefined) : undefined,
        payment_provider: !isInst ? (paymentMode?.options?.provider || null) : undefined,
        payment_reference: !isInst ? (paymentMode?.options?.reference || null) : undefined,
        payment_note: !isInst ? (paymentMode?.options?.note || null) : undefined,
        paid_now: !isInst ? (!!paymentMode?.options?.paid_now) : undefined,
      };

      if (isInst) {
        const opts = paymentMode.options || {};
        const frequency = String(opts.frequency || 'monthly').toLowerCase();
        const count = clampInt(opts.installments_count, 1, 60, 3);
        const startDate = opts.first_due_date || toYYYYMMDD(new Date());

        const plan = {
          frequency,
          start_date: startDate,
          installment_count: count,
          installments: null,
          planned_method: opts.planned_method ? String(opts.planned_method).toLowerCase() : '',
          planned_provider: String(opts.planned_provider || 'orange_money').toLowerCase(),
          planned_reference: opts.planned_reference || '',
          planned_note: opts.planned_note || '',
        };

        if (frequency === 'custom') {
          const cs = Array.isArray(opts.custom_schedule) ? opts.custom_schedule : [];
          const installments = cs
            .map(it => ({
              due_date: toYYYYMMDD(it.due_date),
              amount: Math.round(Number(it.amount || 0)),
            }))
            .filter(it => it.due_date && Number.isFinite(it.amount) && it.amount > 0);

          const sum = installments.reduce((s, it) => s + Math.round(Number(it.amount || 0)), 0);
          const target = Math.round(Number(totalTTC || 0));

          if (installments.length === 0) return alert('Mode custom: ajoute des dates/montants valides.');
          if (sum !== target) return alert(`Mode custom: somme tranches (${sum}) doit égaler TTC (${target}).`);

          plan.installments = installments;
          plan.installment_count = installments.length;
          plan.start_date = installments[0]?.due_date || startDate;
        }

        payload.payment_plan = plan;

        // ✅ always pass initial_* flags for backend fallback
        if (opts.initial_paid_now) {
          const amt = Math.round(Number(opts.initial_amount || 0));
          payload.initial_paid_now = true;
          payload.initial_amount = Number.isFinite(amt) && amt > 0 ? amt : null;
          payload.initial_method = String(opts.initial_method || 'cash').toLowerCase();
          payload.initial_provider = String(opts.initial_provider || 'orange_money').toLowerCase();
          payload.initial_reference = opts.initial_reference || '';
          payload.initial_note = opts.initial_note || '';

          if (Number.isFinite(amt) && amt > 0) {
            payload.initial_payment = {
              amount: amt,
              method: payload.initial_method,
              provider: payload.initial_provider,
              reference: payload.initial_reference,
              note: payload.initial_note,
              paid_at: saleDate || null,
            };
          }
        }
      }

      const data = await apiFetch(`/api/orders?pdf=${wantPdf ? 1 : 0}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (wantPdf && data?.external_id) {
        const blob = await apiFetchBlob(`/api/orders/${data.external_id}/pdf?download=1`);
        downloadBlob(blob, `facture_${data.external_id}.pdf`);
      }

      await reloadProducts?.();

      onCreate?.({
        id: data?.external_id || data?.order_id || 'new',
        created_at: new Date().toISOString(),
        buyer: { name: buyer, phone },
        lines,
      });

      // Reset
      setBuyer('');
      setPhone('');
      setSelectedCustomer(null);
      setCustomerRfm(null);
      setPid('');
      setLines([]);
      setPaymentMode({
        type: 'cash',
        options: { method: 'cash', provider: 'orange_money', reference: '', note: '', paid_now: true },
      });
      setDiscount({ remise_pct: 0, remise_amount: 0 });
      setTvaPct(0);
      setSaleDate(toYYYYMMDD(new Date()));
    } catch (e) {
      alert('Erreur création vente : ' + (e?.message || String(e)));
    }
  }

  return (
    <div className="p-6 space-y-4 min-h-screen bg-slate-50/60">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ventes</h1>
          <p className="text-slate-500 text-sm mt-1">
            Création rapide de vente avec suivi des paiements.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        {/* Client + Tel + Jour de vente */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="space-y-1">
            <CustomerAutocomplete
              buyer={buyer}
              setBuyer={setBuyer}
              phone={phone}
              setPhone={setPhone}
              selectedCustomer={selectedCustomer}
              setSelectedCustomer={setSelectedCustomer}
            />
            {selectedCustomer && !rfmLoading && customerRfm?.segment && (() => {
              const SEG = {
                champion:   { label: 'Champion',    emoji: '🥇', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
                loyal:      { label: 'Fidèle',      emoji: '🥈', cls: 'bg-blue-100 text-blue-800 border-blue-200'         },
                new:        { label: 'Nouveau',     emoji: '🌱', cls: 'bg-violet-100 text-violet-800 border-violet-200'   },
                at_risk:    { label: 'À risque',    emoji: '⚠️', cls: 'bg-amber-100 text-amber-800 border-amber-200'      },
                occasional: { label: 'Occasionnel', emoji: '🔄', cls: 'bg-slate-100 text-slate-700 border-slate-200'      },
                lost:       { label: 'Perdu',       emoji: '💤', cls: 'bg-rose-100 text-rose-800 border-rose-200'         },
              }
              const seg = SEG[customerRfm.segment] || SEG.occasional
              const rawConfig = rfmDiscounts?.[customerRfm.segment]
              // Support nouveau format {discount_pct, min_order, note} et ancien format number
              const discountPct  = typeof rawConfig === 'object' ? Number(rawConfig?.discount_pct || 0) : Number(rawConfig || 0)
              const minOrder     = typeof rawConfig === 'object' ? Number(rawConfig?.min_order || 0) : 0
              const note         = typeof rawConfig === 'object' ? String(rawConfig?.note || '') : ''
              const cartTotal    = totalHT || 0
              const minMet       = minOrder === 0 || cartTotal >= minOrder
              const canApply     = discountPct > 0 && minMet
              return (
                <div className={`rounded-xl border px-3 py-2 text-xs font-semibold ${seg.cls}`}>
                  <div className="flex items-center gap-2">
                    <span>{seg.emoji}</span>
                    <span className="font-black">{seg.label}</span>
                    <span className="text-[10px] opacity-60">R{customerRfm.r_score}·F{customerRfm.f_score}·M{customerRfm.m_score}</span>
                    {discountPct > 0 && (
                      <button
                        type="button"
                        disabled={!minMet}
                        onClick={() => minMet && setDiscount({ remise_pct: discountPct, remise_amount: 0 })}
                        className={`ml-auto flex items-center gap-1 rounded-lg px-2 py-0.5 text-[10px] font-black border border-current transition ${minMet ? 'bg-white bg-opacity-70 hover:bg-opacity-100 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
                      >
                        Appliquer −{discountPct}%
                      </button>
                    )}
                  </div>
                  {(note || (discountPct > 0 && minOrder > 0 && !minMet)) && (
                    <div className="mt-1 flex gap-3 text-[10px] opacity-70">
                      {note && <span>✦ {note}</span>}
                      {discountPct > 0 && minOrder > 0 && !minMet && (
                        <span>Panier min. {Number(minOrder).toLocaleString('fr-FR')} XOF requis</span>
                      )}
                    </div>
                  )}
                </div>
              )
            })()}
            {selectedCustomer && rfmLoading && (
              <div className="text-xs text-slate-400 px-1">Chargement score RFM…</div>
            )}
          </div>

          <input
            value={phone}
            onChange={e => setPhone(e.target.value)}
            className="border border-slate-200 px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
            placeholder="Téléphone"
          />

          <div>
            <label className="text-xs text-slate-500">Jour de vente</label>
            <input
              type="date"
              value={saleDate}
              onChange={e => safeSetSaleDate(e.target.value)}
              className="border border-slate-200 px-3 py-2 rounded-lg w-full focus:ring-2 focus:ring-slate-300 focus:border-slate-400"
            />
            <div className="text-[11px] text-slate-500 mt-1">(saisie rétroactive possible)</div>
          </div>
        </div>

        {/* Sélection produit */}
        <ProductSelector
          products={products}
          pid={pid}
          setPid={setPid}
          qty={qty}
          setQty={setQty}
          variantsLoading={variantsLoading}
          onAddLine={addLine}
        />

        {/* Table du panier */}
        <CartTable
          lines={lines}
          products={products}
          variantsCache={variantsCache}
          onRemove={removeLine}
          onUpdateQty={updateLineQty}
          onUpdateVariant={updateLineVariant}
          onUpdateNotes={updateLineNotes}
        />

        {/* Totaux + TVA + Remise + Paiement */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SalesSummary
            totalHT={totalHT}
            tvaPct={tvaPct}
            setTvaPct={setTvaPct}
            discount={discount}
            setDiscount={setDiscount}
          />

          <PaymentModeSelector totalTTC={totalTTC} value={paymentMode} onChange={setPaymentMode} />
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          <button
            onClick={() => createOrder(false)}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Créer vente
          </button>

          <button
            onClick={() => createOrder(true)}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-950"
          >
            Créer vente + PDF
          </button>
        </div>
      </div>
    </div>
  );
}
